import axios from "axios";

import AdminUserAPI from "../../../api/AdminUserAPI";

import type { QueryClient } from "@tanstack/react-query";

import type {
  AdminUserRolesByScope,
  DeleteAdminUserRolesResponse,
  PutAdminUserRolesResponse,
  RoleByScope,
} from "../../../api/AdminUserAPI.types";

const MUTATION_TYPE = {
  TOGGLE_ADMIN: "TOGGLE_ADMIN",
  DELETE_ROLES: "DELETE_ROLES",
  UPDATE_ROLES: "UPDATE_ROLES",
};

type MutationTypeKeys = keyof typeof MUTATION_TYPE;

type MutationType = (typeof MUTATION_TYPE)[MutationTypeKeys];

export interface MutationContext {
  previousRoles?: AdminUserRolesByScope;
}

export const handleMutate = async ({
  action,
  queryClient,
  userId,
  projectId,
  channelId,
  organizationId,
  roles,
  name,
  newRole = false,
}: {
  action: MutationType;
  queryClient: QueryClient;
  userId: string;
  projectId?: string;
  channelId?: string;
  organizationId?: string;
  roles?: string[];
  newRole?: boolean;
  name?: string;
}): Promise<MutationContext> => {
  const { DELETE_ROLES, UPDATE_ROLES } = MUTATION_TYPE;

  const getAdminUserRolesQueryKey = AdminUserAPI.generateQuery({
    fn: AdminUserAPI.getAdminUserRoles,
    params: [userId],
  }).queryKey;

  await queryClient.cancelQueries(getAdminUserRolesQueryKey);

  const previousRoles: AdminUserRolesByScope | undefined =
    queryClient.getQueryData(getAdminUserRolesQueryKey);

  switch (action) {
    case DELETE_ROLES:
      {
        const newRoles = {
          ...previousRoles,
          organization: previousRoles?.organization?.filter(
            (org) => org.uuid !== organizationId,
          ),
          fast_channels: previousRoles?.fast_channels?.filter(
            (channel) => channel.uuid !== channelId,
          ),
          storefront_projects: previousRoles?.storefront_projects?.filter(
            (project) => project.uuid !== projectId,
          ),
        };
        queryClient.setQueryData(getAdminUserRolesQueryKey, newRoles);
      }
      break;
    case UPDATE_ROLES:
      {
        const updatedRolesByScope = (
          uuid: string,
          rolesByScope: RoleByScope[] = [],
        ): RoleByScope[] => {
          if (!roles) return [];

          if (newRole) {
            return [...rolesByScope, { name: name ?? "", uuid, roles }];
          }

          return rolesByScope
            ?.map((scope) =>
              scope.uuid === uuid ? { ...scope, roles } : scope,
            )
            .filter((scope) => scope.roles?.length > 0);
        };
        const newRoles = {
          organization: organizationId
            ? updatedRolesByScope(organizationId, previousRoles?.organization)
            : previousRoles?.organization,
          fast_channels: channelId
            ? updatedRolesByScope(channelId, previousRoles?.fast_channels)
            : previousRoles?.fast_channels,
          storefront_projects: projectId
            ? updatedRolesByScope(projectId, previousRoles?.storefront_projects)
            : previousRoles?.storefront_projects,
        };
        queryClient.setQueryData(getAdminUserRolesQueryKey, newRoles);
      }
      break;
    default:
  }

  return { previousRoles };
};

export const handleError = ({
  error,
  context,
  userId,
  queryClient,
  errorMessage,
}: {
  error: unknown;
  context: MutationContext;
  userId: string;
  queryClient: QueryClient;
  errorMessage: (statusCode?: number) => void;
}): void => {
  const getAdminUserRolesQueryKey = AdminUserAPI.generateQuery({
    fn: AdminUserAPI.getAdminUserRoles,
    params: [userId],
  }).queryKey;

  queryClient.setQueryData(getAdminUserRolesQueryKey, context.previousRoles);

  if (axios.isAxiosError(error)) {
    errorMessage(error?.response?.status);
  }
};

interface DeleteRolesMutationReturn {
  mutationFn: (
    params: DeleteRolesMutationFnParams,
  ) => Promise<DeleteAdminUserRolesResponse>;
  onMutate: (params: DeleteRolesMutationFnParams) => Promise<MutationContext>;
  onSuccess: () => void;
  onError: (
    error: unknown,
    args: DeleteRolesMutationFnParams,
    context: MutationContext,
  ) => void;
}

export interface DeleteRolesMutationFnParams {
  userId: string;
  projectId?: string;
  channelId?: string;
  organizationId?: string;
}

export const deleteRolesMutation = ({
  queryClient,
  successMessage,
  errorMessage,
}: {
  queryClient: QueryClient;
  successMessage: () => void;
  errorMessage: (statusCode?: number) => void;
}): DeleteRolesMutationReturn => ({
  mutationFn: async ({
    userId,
    projectId,
    channelId,
    organizationId,
  }: DeleteRolesMutationFnParams) =>
    AdminUserAPI.deleteAdminUserRoles({
      userId,
      projectId,
      channelId,
      organizationId,
    }),
  onMutate: async (args: DeleteRolesMutationFnParams) =>
    handleMutate({
      action: MUTATION_TYPE.DELETE_ROLES,
      queryClient,
      ...args,
    }),
  onSuccess: () => successMessage(),
  onError: (
    error: unknown,
    args: DeleteRolesMutationFnParams,
    context: MutationContext,
  ): void =>
    handleError({
      userId: args.userId,
      queryClient,
      errorMessage,
      error,
      context,
    }),
});

interface UpdateRolesMutationReturn {
  mutationFn: (
    params: UpdateRolesMutationFnParams,
  ) => Promise<PutAdminUserRolesResponse>;
  onMutate: (params: UpdateRolesMutationFnParams) => Promise<MutationContext>;
  onSuccess: () => void;
  onError: (
    error: unknown,
    args: UpdateRolesMutationFnParams,
    context: MutationContext,
  ) => void;
}

export interface UpdateRolesMutationFnParams {
  userId: string;
  projectId?: string;
  channelId?: string;
  organizationId?: string;
  roles: string[];
  newRole?: boolean;
  name?: string;
}

export const updateRolesMutation = ({
  queryClient,
  successMessage,
  errorMessage,
}: {
  queryClient: QueryClient;
  successMessage: () => void;
  errorMessage: (statusCode?: number) => void;
}): UpdateRolesMutationReturn => ({
  mutationFn: async ({
    userId,
    projectId,
    channelId,
    organizationId,
    roles,
    newRole,
  }: UpdateRolesMutationFnParams) =>
    newRole
      ? AdminUserAPI.postAdminUserRoles({
          userId,
          projectId,
          channelId,
          organizationId,
          roles,
        })
      : AdminUserAPI.putAdminUserRoles({
          userId,
          projectId,
          channelId,
          organizationId,
          roles,
        }),
  onMutate: async (args: UpdateRolesMutationFnParams) =>
    handleMutate({ action: MUTATION_TYPE.UPDATE_ROLES, queryClient, ...args }),
  onSuccess: () => successMessage(),
  onError: (
    error: unknown,
    args: UpdateRolesMutationFnParams,
    context: MutationContext,
  ) =>
    handleError({
      userId: args.userId,
      queryClient,
      errorMessage,
      error,
      context,
    }),
});
